'use client';

import { usePathname } from 'next/navigation';
import { useState } from 'react';

import type { CookieConsentGroups } from '../../types';
import { DEFAULT_COOKIE_GROUPS } from '../../utils/cookieConsent';
import {
  acceptAllCookieConsent,
  saveCookieConsentGroups
} from '../../utils/cookieConsentActions';
import {
  dismissCookieConsentForSession,
  isFullCookieConsent,
  pushCookieChange,
  pushCookieConsentChoice,
  pushPopupClosed,
  pushPopupSettings
} from '../../utils/cookieConsentEvents';
import { Banner } from '../Banner/Banner';
import { Settings } from '../Settings/Settings';

export interface ConsentProps {
  open?: boolean;
}

export function Consent({ open = true }: ConsentProps) {
  const [bannerOpen, setBannerOpen] = useState(open);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [groups, setGroups] = useState<CookieConsentGroups>(
    DEFAULT_COOKIE_GROUPS
  );

  const pathname = usePathname();
  const isCookiePolicyPage = pathname === '/legal/politika-cookie/';

  if (isCookiePolicyPage) return;

  const closeAll = () => {
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  const onAcceptAll = () => {
    pushCookieConsentChoice(DEFAULT_COOKIE_GROUPS);
    closeAll();
    void acceptAllCookieConsent();
  };

  const onClose = () => {
    setBannerOpen(false);
  };

  const onDismiss = () => {
    pushPopupClosed();
    dismissCookieConsentForSession();
    closeAll();
  };

  const onConfigure = () => {
    pushPopupSettings();
    setGroups(DEFAULT_COOKIE_GROUPS);
    setBannerOpen(false);
    setSettingsOpen(true);
  };

  const onSaveSettings = () => {
    if (isFullCookieConsent(groups)) {
      pushCookieConsentChoice(groups);
      closeAll();
      void saveCookieConsentGroups(groups);
      return;
    }

    pushCookieChange(groups);
    closeAll();
    void saveCookieConsentGroups(groups).then(() => {
      window.location.reload();
    });
  };

  const onCloseSettings = () => {
    setSettingsOpen(false);
    setBannerOpen(true);
  };

  if (!bannerOpen && !settingsOpen) return null;

  return (
    <>
      {bannerOpen && (
        <Banner
          onAcceptAll={onAcceptAll}
          onConfigure={onConfigure}
          onDismiss={onDismiss}
          onClose={onClose}
        />
      )}
      <Settings
        open={settingsOpen}
        groups={groups}
        onGroupsChange={setGroups}
        onSave={onSaveSettings}
        onCancel={onCloseSettings}
        onClose={onCloseSettings}
      />
    </>
  );
}
